import { config, useTransition } from 'react-spring';
import type { UseCategoriesTransitionsProps } from './interfaces';

/**
 * Builds spring transitions for desktop department rows and the selected department.
 *
 * ---
 * 🧠 **Context**
 *
 * - Department entries use staggered opacity and vertical motion while the menu is open.
 * - Changing selectedDepartment.id drives the horizontal category transition.
 *
 * ---
 * @example
 * ```ts
 * const animation = useCategoriesTransitions({
 *   open: true,
 *   data: [{ id: 'medicamentos', name: 'Medicamentos' }],
 *   selectedDepartment: { id: 'medicamentos', name: 'Medicamentos' },
 * });
 * ```
 *
 * ---
 * @function useCategoriesTransitions
 * @param {UseCategoriesTransitionsProps<T>} props - Menu openness, department items and current selection.
 * @returns {ReturnType<typeof useCategoriesTransitions<T>>} Department and category transition render functions.
 */
export function useCategoriesTransitions<T extends { id: string | number }>({
	open,
	data,
	selectedDepartment,
}: UseCategoriesTransitionsProps<T>) {
	const departmentTransitions = useTransition(open ? data?.map((_, index) => index) : [], {
		from: {
			opacity: 0,
			transform: 'translateY(-12px)',
		},
		enter: {
			opacity: 1,
			transform: 'translateY(0px)',
		},
		leave: {
			opacity: 0,
			transform: 'translateY(-12px)',
		},
		trail: 90,
		delay: 120,
		config: config.stiff,
	});

	const categoriesTransition = useTransition(selectedDepartment?.id, {
		from: {
			opacity: 0,
			transform: 'translateX(-32px)',
		},
		enter: {
			opacity: 1,
			transform: 'translateX(0px)',
		},
		config: {
			tension: 210,
			friction: 20,
			duration: 150,
		},
	});

	return {
		departmentTransitions,
		categoriesTransition,
	};
}
